import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AttachmentTypeService } from '../../core/services/attachment-type.service';
import { ToastService } from '../../core/services/toast.service';
import { AuthService } from '../../core/services/auth.service';
import { AttachmentTypeDto, CreateAttachmentTypeRequest } from '../../core/models/models';

@Component({
  selector: 'app-attachment-types',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="space-y-6 font-sans">
      <!-- Header Section -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 class="text-xl font-extrabold text-slate-900 flex items-center gap-2">
            <svg class="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            Compliance Attachment Types
          </h1>
          <p class="text-xs text-slate-500 mt-1">
            Manage legal documentation definitions, Data Sharing Agreements (DSA), and privacy compliance attachments that institutions can require on their services.
          </p>
        </div>

        <button
          (click)="openAddModal()"
          class="px-4 py-2.5 rounded-xl text-xs font-bold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-md shadow-emerald-600/20 flex items-center gap-2 transition cursor-pointer"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Document Type
        </button>
      </div>

      <!-- Attachment Table -->
      <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div *ngIf="isLoading()" class="p-8 text-center text-slate-500 text-xs">
          <div class="inline-block animate-spin w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full mb-2"></div>
          <p>Loading compliance attachment types...</p>
        </div>

        <table *ngIf="!isLoading()" class="w-full text-left text-xs text-slate-700">
          <thead class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
            <tr>
              <th class="p-4">Type Code</th>
              <th class="p-4">Document Title</th>
              <th class="p-4">Scope</th>
              <th class="p-4">Allowed Formats</th>
              <th class="p-4">Max Upload Size</th>
              <th class="p-4">Mandatory Policy</th>
              <th class="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200/80">
            <tr *ngFor="let doc of documentTypes()" class="hover:bg-emerald-50/30 transition">
              <td class="p-4 font-mono font-bold text-emerald-700">{{ doc.code }}</td>
              <td class="p-4 font-bold text-slate-900">
                {{ doc.name }}
                <div class="text-[10px] font-normal text-slate-500 mt-0.5">{{ doc.description || 'No description provided' }}</div>
              </td>
              <td class="p-4">
                <span
                  class="px-2 py-0.5 rounded-md text-[10px] font-semibold border"
                  [ngClass]="doc.institutionName === 'Global Template' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-amber-50 text-amber-700 border-amber-200'"
                >
                  {{ doc.institutionName || 'Global Template' }}
                </span>
              </td>
              <td class="p-4">
                <span
                  *ngFor="let ext of getExtensionList(doc.allowedExtensions)"
                  class="inline-block mr-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] uppercase font-mono font-bold"
                >
                  .{{ ext }}
                </span>
              </td>
              <td class="p-4 font-semibold text-slate-800">{{ doc.maxSizeMb || 10 }} MB</td>
              <td class="p-4">
                <span *ngIf="doc.isRequired" class="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200">
                  Mandatory
                </span>
                <span *ngIf="!doc.isRequired" class="px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200">
                  Optional
                </span>
              </td>
              <td class="p-4 text-right">
                <button
                  (click)="deleteType(doc.id)"
                  [disabled]="isDeleting() === doc.id"
                  class="text-rose-600 hover:text-rose-800 font-bold text-xs cursor-pointer transition disabled:opacity-50"
                >
                  {{ isDeleting() === doc.id ? 'Deleting...' : 'Remove' }}
                </button>
              </td>
            </tr>
            <tr *ngIf="documentTypes().length === 0">
              <td colspan="7" class="p-8 text-center text-slate-400 text-xs italic">
                No defined attachment types found. Click "Add Document Type" to define custom compliance requirements.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Add Modal -->
      <div *ngIf="showModal()" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
        <div class="bg-white border border-slate-200 rounded-2xl max-w-md w-full p-6 space-y-4 shadow-2xl">
          <div class="flex items-center justify-between border-b border-slate-200 pb-3">
            <h3 class="text-base font-bold text-slate-900">Add Compliance Document Type</h3>
            <button (click)="showModal.set(false)" class="text-slate-400 hover:text-slate-900 cursor-pointer text-lg leading-none">✕</button>
          </div>

          <div class="space-y-3 text-xs">
            <div>
              <label class="block text-slate-700 font-bold mb-1">Code (Optional)</label>
              <input
                type="text"
                [(ngModel)]="newDoc.code"
                placeholder="e.g. DOC_DPIA"
                class="w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 focus:border-emerald-500 focus:outline-none font-mono text-xs"
              />
            </div>

            <div>
              <label class="block text-slate-700 font-bold mb-1">Document Title *</label>
              <input
                type="text"
                [(ngModel)]="newDoc.name"
                placeholder="Data Protection Impact Assessment (DPIA)"
                class="w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 focus:border-emerald-500 focus:outline-none text-xs"
              />
            </div>

            <div>
              <label class="block text-slate-700 font-bold mb-1">Description</label>
              <textarea
                [(ngModel)]="newDoc.description"
                rows="2"
                placeholder="Brief guidelines on required document content..."
                class="w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 focus:border-emerald-500 focus:outline-none text-xs"
              ></textarea>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-slate-700 font-bold mb-1">Allowed Extensions</label>
                <input
                  type="text"
                  [(ngModel)]="newDoc.allowedExtensions"
                  placeholder="pdf,docx"
                  class="w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 focus:border-emerald-500 focus:outline-none font-mono text-xs"
                />
              </div>

              <div>
                <label class="block text-slate-700 font-bold mb-1">Max Size (MB)</label>
                <input
                  type="number"
                  [(ngModel)]="newDoc.maxSizeMb"
                  min="1"
                  max="100"
                  class="w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 focus:border-emerald-500 focus:outline-none font-mono text-xs"
                />
              </div>
            </div>

            <div class="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                id="reqCheck"
                [(ngModel)]="newDoc.isRequired"
                class="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
              />
              <label for="reqCheck" class="text-slate-700 font-bold cursor-pointer">Mark as Mandatory Document by Default</label>
            </div>
          </div>

          <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              (click)="showModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300 cursor-pointer"
            >
              Cancel
            </button>
            <button
              (click)="saveDocType()"
              [disabled]="isSaving()"
              class="px-4 py-2 rounded-xl text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 shadow-sm cursor-pointer disabled:opacity-50"
            >
              {{ isSaving() ? 'Saving...' : 'Save Document Type' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class AttachmentTypesComponent implements OnInit {
  private attachmentTypeService = inject(AttachmentTypeService);
  private toastService = inject(ToastService);
  authService = inject(AuthService);

  showModal = signal(false);
  isLoading = signal(false);
  isSaving = signal(false);
  isDeleting = signal<string | null>(null);

  documentTypes = signal<AttachmentTypeDto[]>([]);

  newDoc: CreateAttachmentTypeRequest = {
    code: '',
    name: '',
    description: '',
    isRequired: true,
    allowedExtensions: 'pdf,docx',
    maxSizeMb: 10
  };

  ngOnInit() {
    this.loadAttachmentTypes();
  }

  loadAttachmentTypes() {
    this.isLoading.set(true);
    this.attachmentTypeService.getAllAttachmentTypes().subscribe({
      next: (res) => {
        this.isLoading.set(false);
        if (res.success && res.data) {
          this.documentTypes.set(res.data);
        }
      },
      error: (err) => {
        this.isLoading.set(false);
        this.toastService.error('Failed to load compliance attachment types');
      }
    });
  }

  openAddModal() {
    this.newDoc = {
      code: '',
      name: '',
      description: '',
      isRequired: true,
      allowedExtensions: 'pdf,docx',
      maxSizeMb: 10
    };
    this.showModal.set(true);
  }

  saveDocType() {
    if (!this.newDoc.name?.trim()) {
      this.toastService.error('Document title is required');
      return;
    }

    this.isSaving.set(true);
    const payload: CreateAttachmentTypeRequest = {
      code: this.newDoc.code?.trim() || undefined,
      name: this.newDoc.name.trim(),
      description: this.newDoc.description?.trim() || undefined,
      isRequired: this.newDoc.isRequired ?? true,
      allowedExtensions: this.newDoc.allowedExtensions?.trim() || 'pdf,docx',
      maxSizeMb: this.newDoc.maxSizeMb ? Number(this.newDoc.maxSizeMb) : 10
    };
    this.attachmentTypeService.createAttachmentType(payload).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        if (res.success && res.data) {
          this.toastService.success('Attachment type created successfully');
          this.showModal.set(false);
          this.loadAttachmentTypes();
        } else {
          this.toastService.error(res.message || 'Failed to create attachment type');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toastService.error(err.error?.message || 'Error creating attachment type');
      }
    });
  }

  async deleteType(id: string) {
    const doc = this.documentTypes().find(d => d.id === id);
    const title = doc?.name ? ` "${doc.name}"` : '';

    const confirmed = await this.toastService.confirm({
      title: 'Remove Compliance Document Type',
      message: `Are you sure you want to remove compliance attachment type${title}?`,
      confirmText: 'Yes, Remove',
      cancelText: 'Cancel',
      confirmStyle: 'danger'
    });
    if (!confirmed) return;

    this.isDeleting.set(id);
    this.attachmentTypeService.deleteAttachmentType(id).subscribe({
      next: (res) => {
        this.isDeleting.set(null);
        if (res.success) {
          this.toastService.success('Attachment type removed');
          this.loadAttachmentTypes();
        } else {
          this.toastService.error(res.message || 'Failed to remove attachment type');
        }
      },
      error: (err) => {
        this.isDeleting.set(null);
        this.toastService.error(err.error?.message || 'Error deleting attachment type');
      }
    });
  }

  getExtensionList(exts?: string): string[] {
    if (!exts) return ['PDF'];
    return exts
      .split(',')
      .map(e => e.trim().toLowerCase())
      .filter(e => e.length > 0);
  }
}
